import React, { useState, useEffect } from "react";
import { variantSelect, productSelect } from "@/store";
import {
  usePdpActions,
  usePdpSellingPlan,
  usePdpProduct,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { CartAddLineItem, shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "@/features/product/SubscriptionSelector";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";

import { AddToCartProps, VariantSizeOptionsGroup } from "./AddToCart";

export const AddToCartProductCard = ({
  cartSubmitId,
  addToCartText = "Add to Cart",
  outOfStockText = "Out of Stock",
  onAddToCartSuccess,
}: AddToCartProps) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = usePdpVariant();
  const actions = usePdpActions();
  const subscription = variantSelect.orderableSubscription(variant);
  const product = usePdpProduct();
  const sellingPlan = usePdpSellingPlan();

  const isBundle =
    product?.tags?.includes("bundle") &&
    !product.handle.includes("starter-kit");

  // State for subscription toggle
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    sellingPlan ? "subscription" : "one-time"
  );

  // Track selected variant index for quantity-based adds
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  // Calculate the subscription price using the centralized logic
  const subscriptionPrice = variantSelect.lowestPrice(variant, product);
  const onetimePrice = variantSelect.onetimePrice(variant, product);

  // Define the displayed price based on subscription toggle
  const displayedPrice =
    subToggle === "subscription" ? subscriptionPrice : onetimePrice;

  // Use the new promo price utility
  const atcCTAPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: displayedPrice.asCents(),
    sellingPlan,
    variant,
    product,
    currency: "$",
    strikeThroughSize: "text-xs",
    priceSize: "text-sm",
    bannerPadding: "px-3 py-2",
  });

  const addToCartPrice = (
    <>
      {variant?.price != undefined ? (
        <div className="flex flex-wrap items-center space-x-2 lg:justify-end">
          <p
            data-id="product-card-price"
            className={`body ml-1 text-xs font-semibold text-white ${
              variant?.size || variant?.subscription ? "order-2" : ""
            }`}
          >
            <span className="sr-only">Current price</span>
            {atcCTAPrice.display}
          </p>
        </div>
      ) : null}
    </>
  );

  // Update subscription preference when subToggle changes
  useEffect(() => {
    actions.setSubscriptionPreference(subToggle === "subscription");
  }, [subToggle, actions]);

  // Sync selectedVariantIndex when variant changes externally (e.g., URL params)
  useEffect(() => {
    const currentIndex = product.variants.findIndex((v) => v.id === variant.id);
    if (currentIndex !== -1) {
      setSelectedVariantIndex(currentIndex);
    }
  }, [variant.id, product.variants]);

  const { variantToAdd, quantity } =
    variantSelect.resolveVariantAndQuantityForCartAdd(
      variant,
      product.variants
    );

  // Determine the subscription plan for the variant being added
  const variantToAddSubscription =
    variantSelect.orderableSubscription(variantToAdd);
  const sellingPlanToUse =
    subToggle === "subscription" && variantToAddSubscription
      ? sellingPlan || productSelect.defaultSellingPlan(product)
      : undefined;

  return (
    <>
      {(variant?.size || variant?.subscription || variant?.price) && (
        <div className="plp-box-sizes_variant-options mt-4">
          <VariantSizeOptionsGroup
            onVariantIndexChange={setSelectedVariantIndex}
          />
        </div>
      )}

      <div className="w-full">
        {subscription && (
          <div className="pt-3">
            <SubscriptionSelector
              value={subToggle}
              onChange={(value) => {
                setSubToggle(value);
              }}
              size="sm"
              cta={
                <p className="body w-full text-center text-xs">
                  <span>
                    {variantSelect.subscriptionDiscount(variant, product) >
                    0 ? (
                      <>
                        Save{" "}
                        {variantSelect.subscriptionDiscount(variant, product)}%
                        when you subscribe.
                      </>
                    ) : null}{" "}
                    Ships every 4 months.
                  </span>
                </p>
              }
            />
          </div>
        )}

        <div className="mt-4 flex w-full flex-col-reverse items-center justify-between space-x-0 lg:mt-1 lg:space-y-2">
          <Button
            className="learn-more-card-link mt-1 items-center space-x-2"
            component="a"
            size="sm"
            variant="ghost"
            href={`/products/${product.handle}?variant=${variant.id}`}
            aria-label={`Learn more about ${product.title}`}
          >
            <InfoOutlined className="h-4 w-4 text-inherit" />{" "}
            <span>More details</span>
          </Button>
          <Button
            id={cartSubmitId}
            className="lg:btn-sm xl:btn-base btn-base w-full"
            disabled={!variant.available}
            onClick={() => {
              const lineItem: CartAddLineItem[] = [
                variantSelect.cartAddLineItem(variantToAdd, {
                  subscription: sellingPlanToUse || false,
                  quantity,
                }),
              ];
              const sourceModule = isBundle
                ? "bundle_plp_quick_add"
                : "plp_quick_add";
              const body = { items: lineItem, sourceModule };
              shopifyCartAdder.mutate(body, {
                onSuccess: () => {
                  cartActions.openDrawer();
                  if (onAddToCartSuccess) {
                    onAddToCartSuccess();
                  }
                },
              });
            }}
          >
            <div className="flex w-full items-center justify-between text-xs">
              <span>{variant.available ? addToCartText : outOfStockText}</span>
              {addToCartPrice}
            </div>
          </Button>
        </div>
        {!variant.available && (
          <div className="basis-full pt-4">
            <Button
              className="BIS_trigger flex w-full"
              aria-label="notify me when restocked"
              size="lg"
              data-product-data={JSON.stringify({
                id: product.id,
                available: false,
                title: product.title,
                handle: product.handle,
                tags: product.tags,
                variants: product.variants.map((v) => ({
                  id: v.id,
                  title: v.title,
                  available: false,
                  inventory_management: "shopify",
                  inventory_quantity: v.inventoryQuantity,
                })),
              })}
              data-variant-id={variant.id}
              data-product-handle={product.handle}
            >
              Notify Me When Restocked
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
